import { Form, FormSelect, StandardFormButtons } from '@components/form';
import { FormTagsInput } from '@components/form/FormInputs/FormTagsInput';
import {
  useCreateAnnotationMutation,
  useGetAnnotationFormatsQuery,
  useGetAnnotationMapsQuery,
} from '@generated/graphql';
import { MenuItem, Stack, Typography } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';
import * as yup from 'yup';

const formInitialValues = {
  format: '',
  mapping: 'none',
  tags: [],
};

const validationSchema = yup.object({
  format: yup.string().required('Format is required'),
  mapping: yup.string().required('Map is required'),
  tags: yup.array().of(yup.string()),
});
type FormValues = yup.InferType<typeof validationSchema>;

type DatasetsDetailsAnnotationsCreateAnnotationProps = {
  datasetId: string;
  workspaceId: string;
  onClose: VoidFunction;
  refetch: VoidFunction;
};

export const DatasetsDetailsAnnotationsCreateAnnotation = ({
  datasetId,
  workspaceId,
  onClose,
  refetch,
}: DatasetsDetailsAnnotationsCreateAnnotationProps) => {
  const { createAsyncNotification } = useNotifications();
  const [createAnnotation] = useCreateAnnotationMutation();

  const { data: annotationFormats, loading: annotationFormatsLoading } =
    useGetAnnotationFormatsQuery();
  const { data: annotationMaps, loading: annotationMapsLoading } = useGetAnnotationMapsQuery({
    variables: { workspaceId },
  });
  const sortedAnnotationFormats =
    !annotationFormatsLoading &&
    [...(annotationFormats?.getAnnotationFormats || [])].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  const sortedAnnotationMaps =
    !annotationMapsLoading &&
    [...(annotationMaps?.getAnnotationMaps || [])].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );

  const handleSaveButton = createAsyncNotification(
    'Successfully created annotation.',
    async ({ format, mapping, tags }: { format: string; mapping: string; tags: string[] }) => {
      onClose();
      await createAnnotation({
        variables: {
          datasetId,
          workspaceId,
          format,
          mapId: mapping === 'none' ? null : mapping,
          tags,
        },
      });
      posthog.capture('user_create-annotation_submitted');
      void refetch();
    },
  );

  const mappingName = (id: string) => {
    const mapping = (sortedAnnotationMaps || []).find((m) => m.mapId === id);
    return mapping?.name;
  };

  return (
    <Form<FormValues>
      validateOnBlur={false}
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSaveButton}
    >
      <Stack>
        <FormSelect name="format" label="Format" disabled={annotationFormatsLoading}>
          {(sortedAnnotationFormats || []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </FormSelect>
        <FormSelect name="mapping" label="Mapping" disabled={annotationMapsLoading}>
          <MenuItem value="none">None</MenuItem>
          {(sortedAnnotationMaps || []).map((annotationMap) => (
            <MenuItem key={annotationMap.mapId} value={annotationMap.mapId}>
              {mappingName(annotationMap.mapId)}
            </MenuItem>
          ))}
        </FormSelect>
        <FormTagsInput name="tags" />
        <StandardFormButtons onClose={onClose} />
      </Stack>
    </Form>
  );
};

export const DatasetsDetailsAnnotationsCreateAnnotationHelpContent = (
  <Stack gap={1}>
    <Typography variant="body2">
      Annotations are a description of what is in the imagery, usually in a format that a machine
      learning algorithm can ingest. The annotations generated are a proprietary format that needs
      to be converted before being ingested by a machine learning algorithm. For this reason, we
      provide a service to convert our annotations to several common formats.
    </Typography>
    <Typography variant="body2">
      During the conversion of annotations, we offer a way to map objects to specific classes. This
      can be helpful when you have several objects of one type in imagery that you want to be
      classified into a single class.
    </Typography>
  </Stack>
);
